import { v4 as uuidv4 } from 'uuid';
import { Page } from '~/models/page.model';
import type { RootStore } from '~/stores/root.store';
import {
    CommandType,
    ICreateCommand,
    IDeleteCommand,
    TBlock,
    TCommand
} from '~/types/builder.types';
import {
    _DuplicateBlockOptions,
    createBlock,
    createBlockInstance,
    deleteBlock,
    duplicateRootBlock
} from '~/utils/block.utils';
import { IPageResponse, ITransformedPageResponse } from '~/types/server.types';
import {
    createCreateBlockCommand,
    createCreatePageCommand,
    createUpdatePageCommand,
    createDeletePageCommand,
    extractOldData,
    createDeleteBlockCommand
} from '~/utils/commands.utils';
import { IBasicCommandsActionValue } from '~/services/actions-manager/action';
import type { ContainerBlock } from '~/models/blocks/container-block.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import { falseNotZero, mergeArrayFields } from '~/utils/lodash.utils';
import { Direction } from '~/types/app.types';
import { inRange } from 'lodash';

export function getPageIndexBySchema(schema: string) {
    const schemaSplits = schema.split('-');
    if (schemaSplits.length < 2) {
        return null;
    }

    return parseInt(schemaSplits[0], 10) - 1;
}

export function getDuplicatePageOrder(pageToDuplicate, pages) {
    const pageIndex = pages.findIndex(item => item.page_id === pageToDuplicate);
    const nextPageIndex = pageIndex + 1;

    if (!pages[nextPageIndex]) {
        return pages[pageIndex].order + 1;
    }

    return (pages[pageIndex].order + pages[nextPageIndex].order) / 2;
}

// TODO: Refactor this file into a manager and then probably separate this function
// somewhere else...
export function createPage(
    pageData: Partial<ITransformedPageResponse>,
    flowId: string,
    flowRevision: FlowRevision<RootStore>,
    rootStore: RootStore
): TCommand[] {
    const pageId = uuidv4();

    const rootBlock = createBlockInstance(
        {
            block_id: uuidv4(),
            page_id: pageId,
            flow_id: flowId,
            type: 'container_block',
            styles: {
                background_color: '#ffffff',
                background_color_opacity: 0
            }
        },
        flowRevision,
        rootStore
    );

    const pageTitle =
        pageData.title ||
        `Page ${rootStore.builderUiStore.selectedFlow?.pages?.length + 1}`;
    const newPage = new Page(
        {
            page_id: pageId,
            flow_id: flowId,
            root_block_id: rootBlock.block_id,
            order: pageData.order,
            cta_text: pageData.cta_text || 'Next',
            completed: pageData.completed,
            redirect_website_url: pageData.redirect_website_url, // TODO: Change when website URL is active
            title: pageTitle,
            views: []
        },
        flowRevision,
        rootStore
    );

    return [
        createCreateBlockCommand(rootBlock),
        createCreatePageCommand(newPage)
    ];
}

interface IDuplicatePageOptions extends _DuplicateBlockOptions {
    completeCopy?: boolean;
    isImporting?: boolean;
}

const defaultDuplicatePageOptions: IDuplicatePageOptions = {
    completeCopy: false,
    isImporting: false
};

interface IDuplicatePageValue {
    createdBlocks: Array<TBlock>;
    commands: TCommand[];
}

export function duplicatePage(
    page: Page,
    order: number,
    flowId: string,
    flowRevision: FlowRevision<RootStore>,
    rootStore: RootStore,
    {
        completeCopy = defaultDuplicatePageOptions.completeCopy,
        isImporting = defaultDuplicatePageOptions.isImporting,
        withAttributions
    }: IDuplicatePageOptions = defaultDuplicatePageOptions
): IDuplicatePageValue {
    const pageId = completeCopy ? page.page_id : uuidv4();
    const pageTitle =
        completeCopy || isImporting ? page.title : `Copy of ${page.title}`;

    const newRootBlockData = {
        block_id: completeCopy ? page.root_block_id : uuidv4(),
        page_id: pageId,
        flow_id: flowId
    };

    const { duplicatedBlock, createdBlocks, rootBlockCommands } =
        duplicateRootBlock(page.rootBlock, rootStore, newRootBlockData, {
            completeCopy,
            isImporting,
            withAttributions
        });

    const newPage = new Page(
        {
            page_id: pageId,
            flow_id: flowId,
            root_block_id: duplicatedBlock.block_id,
            order,
            cta_text: page.cta_text || 'Next',
            completed: page.completed,
            redirect_website_url: page.redirect_website_url, // TODO: Change when website URL is active
            title: pageTitle,
            views: []
        },
        flowRevision,
        rootStore
    );

    return {
        commands: [createCreatePageCommand(newPage), ...rootBlockCommands],
        createdBlocks
    };
}

export function updatePage(
    page: Page,
    pageData: Partial<IPageResponse>,
    pageOldData: Partial<IPageResponse> = null
): IBasicCommandsActionValue {
    pageData.page_id = page.page_id;

    const oldPageData = pageOldData || extractOldData(pageData, page);

    return {
        commands: [createUpdatePageCommand(page.page_id, pageData)],
        revertCommands: [createUpdatePageCommand(page.page_id, oldPageData)]
    };
}

interface IDeleteBlockOptions {
    shouldCreateRevertCommands?: boolean;
}

const defaultDeleteBlockOptions: IDeleteBlockOptions = {
    shouldCreateRevertCommands: true
};

export function getDeletePageCommands(
    pageId: string,
    rootBlock: TBlock,
    rootStore: RootStore
) {
    const { commands: blocksDeleteCommands } = deleteBlock(
        rootBlock,
        rootStore,
        {
            shouldCreateRevertCommands: false
        }
    );

    return [...blocksDeleteCommands, createDeletePageCommand(pageId)];
}

export function createRevertForCreateCommands(commands: TCommand[]) {
    return commands
        .filter(x => x.type === CommandType.Create)
        .reduce((resultCommands: IDeleteCommand[], command: TCommand) => {
            if ('block_id' in (command as ICreateCommand).data) {
                resultCommands.push(
                    createDeleteBlockCommand(
                        ((command as ICreateCommand).data as TBlock).block_id
                    )
                );
            } else {
                resultCommands.push(
                    createDeletePageCommand(
                        ((command as ICreateCommand).data as Page).page_id
                    )
                );
            }
            return resultCommands;
        }, []);
}

export function deletePage(
    pageId: string,
    rootBlockId: string,
    rootStore: RootStore,
    {
        shouldCreateRevertCommands = true
    }: IDeleteBlockOptions = defaultDeleteBlockOptions
): IBasicCommandsActionValue {
    const {
        builderUiStore: { selectedFlowId, selectedFlow },
        blockCtrl
    } = rootStore;

    const { draftRevision } = selectedFlow;
    const page = draftRevision.pagesMap[pageId];
    const revertCommands = shouldCreateRevertCommands
        ? duplicatePage(
              page,
              page.order,
              selectedFlowId,
              draftRevision,
              rootStore,
              {
                  completeCopy: true
              }
          ).commands
        : [];

    const { rootBlock } = page;
    const commands = getDeletePageCommands(pageId, rootBlock, rootStore);
    const deletePageCommands = { commands, revertCommands };

    mergeArrayFields(
        deletePageCommands,
        blockCtrl.getDeletePageOrBlockSideEffectCommands(page)
    );

    return deletePageCommands;
}

export function calculatePageOrder({
    pages,
    relativeToPage,
    shouldAddBeforePage = false
}: {
    pages: Page[];
    relativeToPage?: Pick<Page, 'order' | 'page_id'>;
    shouldAddBeforePage?: boolean;
}) {
    if (!relativeToPage) {
        return undefined;
    }
    const currentPageIndex = pages.findIndex(
        p => p.page_id === relativeToPage.page_id
    );
    const currentPageOrder = relativeToPage?.order;
    const relativePageOrder = shouldAddBeforePage
        ? pages[currentPageIndex - 1]?.order
        : pages[currentPageIndex + 1]?.order;
    if (!falseNotZero(relativePageOrder)) {
        return (currentPageOrder + relativePageOrder) / 2;
    }
    return shouldAddBeforePage ? currentPageOrder - 1 : currentPageOrder + 1;
}

export const ChargePageDefaultName = 'Payment';

export function createPageForCharge(
    flowId: string,
    flowRevision: FlowRevision<RootStore>,
    pages: Page[],
    selectedPage: Page,
    rootStore: RootStore
): {
    newPage: Page;
    rootBlockOfPage: ContainerBlock;
    newBlocksMap: Record<string, TBlock>;
} {
    const averageOrder = calculatePageOrder({
        pages,
        relativeToPage: selectedPage
    });

    // Create Page

    const pageId = uuidv4();

    const rootBlockOfPage = <ContainerBlock>createBlockInstance(
        {
            block_id: uuidv4(),
            page_id: pageId,
            flow_id: flowId,
            type: 'container_block',
            styles: {
                background_color: '#ffffff',
                background_color_opacity: 0
            }
        },
        flowRevision,
        rootStore
    );

    const newPage = new Page(
        {
            page_id: pageId,
            flow_id: flowId,
            root_block_id: rootBlockOfPage.block_id,
            order: averageOrder,
            cta_text: 'Next',
            completed: undefined,
            redirect_website_url: undefined,
            title: ChargePageDefaultName,
            views: []
        },
        flowRevision,
        rootStore
    );

    const { newBlocksMap, newChildLayout } = createBlock(
        rootBlockOfPage as ContainerBlock,
        'charge_container_block',
        1,
        rootStore
    );

    rootBlockOfPage.children = [newChildLayout];

    return { newPage, rootBlockOfPage, newBlocksMap };
}

export const getNewPageOrder = ({
    relativeToPage,
    pageToMove,
    direction,
    pages
}: {
    relativeToPage: Page;
    pageToMove: Page;
    direction: 'top' | 'bottom';
    pages: Page[];
}) => {
    const relativeToPageIndex = pages.findIndex(
        page => page.page_id === relativeToPage.page_id
    );

    // Find sibling of drop target (according to direction)
    // Then, the new order should be a number between the drop target and it's sibling
    const siblingIndexDirection = direction === Direction.Bottom ? 1 : -1;
    const relativeToPageSibling =
        pages[relativeToPageIndex + siblingIndexDirection];
    const relativeToPageSiblingOrder = relativeToPageSibling
        ? relativeToPageSibling.order
        : relativeToPage.order + siblingIndexDirection;

    // If dragged item is already position in between these targets no need to set it's order again
    if (
        inRange(
            pageToMove.order,
            relativeToPage.order,
            relativeToPageSiblingOrder
        ) ||
        pageToMove.order === relativeToPageSiblingOrder
    ) {
        return relativeToPageSiblingOrder;
    }

    return (relativeToPage.order + relativeToPageSiblingOrder) / 2;
};
